import React, { useEffect, useState } from 'react';
import { useTasks } from '../../context/TasksContext';
import { NotificationRule, NotificationSettings, taskApi, taskErrorMessage } from '../../utils/taskApi';
import { userName } from '../../utils/taskUsers';
import { UserBubble } from './UserBubble';
import '../../tasks.css';

const CATEGORIES: { category: NotificationRule['category']; title: string; description: string; lead?: string }[] = [
  {
    category: 'restock_dates',
    title: 'Website message notifications',
    description:
      "When a product's est. restock date is about to lapse (the website message would fall back to \"Contact us for an ETA\"), add a task to the weekly list. It closes itself once the date is updated.",
    lead: 'days before the date',
  },
  {
    category: 'inputs_low',
    title: 'Inputs reminder notifications',
    description:
      'When an input (thank-you cards, boxes…) reaches its reorder window — the same "low" status as the Inputs page — add a reorder task. It closes itself once stock is back up.',
  },
  {
    category: 'warehouse_requests',
    title: 'Warehouse request notifications',
    description:
      "When a warehouse user sends a Custom Request from the Inputs page, it's added to the weekly list as New, assigned to this person along with whoever asked. The requester is emailed when it's ticked off and whenever someone leaves a note on it — about 10 minutes later, so a tick and a note go out as one email. Finished requests move to the archive after a week. Switched off, requests still reach the weekly list — just unassigned.",
  },
  {
    category: 'holidays',
    title: 'Holiday reminders',
    description:
      'Before each holiday in the Holidays section below — and before the Dec 24 – Jan 2 break — add "Set holiday notification on website / voicemail" to the week list.',
    lead: 'days before the holiday',
  },
];

/** Admin › Notifications: who receives which auto-created tasks, and the morning digest. */
const TaskNotificationSettings: React.FC = () => {
  const { users, ready } = useTasks();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready) return;
    taskApi
      .notificationSettings()
      .then(setSettings)
      .catch((e) => setStatus({ kind: 'error', text: taskErrorMessage(e, 'Could not load notification settings') }));
  }, [ready]);

  if (!ready || !settings) {
    return <p className="section-description">{status?.text || 'Loading…'}</p>;
  }

  const rule = (category: NotificationRule['category']): NotificationRule =>
    settings.rules.find((r) => r.category === category) || { category, enabled: true, assignee_email: null, lead_days: 7 };

  const setRule = (category: NotificationRule['category'], patch: Partial<NotificationRule>) =>
    setSettings({
      ...settings,
      rules: [...settings.rules.filter((r) => r.category !== category), { ...rule(category), ...patch }],
    });

  // A user with no saved row: no digest; on the weekly list if they're an
  // admin (api/utils/tasks/core.ts visibleListSql has the same default).
  const pref = (email: string) =>
    settings.prefs.find((p) => p.email === email) || {
      email,
      digest_enabled: false,
      weekly_list: users.find((u) => u.email === email)?.user_type === 'Admin',
    };
  const setPref = (email: string, patch: Partial<NotificationSettings['prefs'][number]>) =>
    setSettings({
      ...settings,
      prefs: [...settings.prefs.filter((p) => p.email !== email), { ...pref(email), ...patch }],
    });
  const digestOn = (email: string) => pref(email).digest_enabled;
  const weeklyOn = (email: string) => pref(email).weekly_list !== false;

  const peopleList = (isOn: (email: string) => boolean, onToggle: (email: string, on: boolean) => void) => (
    <ul className="tn-digest">
      {users.map((u) => (
        <li key={u.email}>
          <label className="tn-digest-row">
            <input type="checkbox" checked={isOn(u.email)} onChange={(e) => onToggle(u.email, e.target.checked)} />
            <UserBubble users={users} email={u.email} />
            <span>{userName(users, u.email)}</span>
            <span className="tk-muted tn-email">{u.email}</span>
          </label>
        </li>
      ))}
    </ul>
  );

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const next = await taskApi.saveNotificationSettings({
        rules: CATEGORIES.map((c) => rule(c.category)),
        prefs: users.map((u) => ({ email: u.email, digest_enabled: digestOn(u.email), weekly_list: weeklyOn(u.email) })),
      });
      setSettings(next);
      setStatus({ kind: 'ok', text: 'Notification settings saved.' });
    } catch (e) {
      setStatus({ kind: 'error', text: taskErrorMessage(e, 'Could not save') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tn-settings">
      {CATEGORIES.map((c) => {
        const r = rule(c.category);
        return (
          <div key={c.category} className={`tn-rule${r.enabled ? '' : ' is-off'}`}>
            <div className="tn-rule-text">
              <label className="tn-toggle">
                <input type="checkbox" checked={r.enabled} onChange={(e) => setRule(c.category, { enabled: e.target.checked })} />
                <span className="tn-rule-title">{c.title}</span>
              </label>
              <p className="tn-rule-desc">{c.description}</p>
            </div>
            <div className="tn-rule-controls">
              <label className="tk-field">
                <span className="tk-field-label">Assign to</span>
                <select
                  className="tk-input"
                  value={r.assignee_email || ''}
                  onChange={(e) => setRule(c.category, { assignee_email: e.target.value || null })}
                  disabled={!r.enabled}
                >
                  <option value="">Nobody (unassigned)</option>
                  {users.map((u) => (
                    <option key={u.email} value={u.email}>{userName(users, u.email)}</option>
                  ))}
                </select>
              </label>
              {c.lead && (
                <label className="tk-field tn-lead">
                  <span className="tk-field-label">Warn</span>
                  <span className="tn-lead-row">
                    <input
                      type="number"
                      min={0}
                      max={90}
                      className="tk-input tk-input-num"
                      value={r.lead_days}
                      onChange={(e) => setRule(c.category, { lead_days: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                      disabled={!r.enabled}
                    />
                    <span className="tk-muted">{c.lead}</span>
                  </span>
                </label>
              )}
            </div>
          </div>
        );
      })}

      <div className="tn-rule">
        <div className="tn-rule-text">
          <span className="tn-rule-title">Who gets the MPP weekly list</span>
          <p className="tn-rule-desc">
            The "MPP Week of…" list shows up — pinned — for the people ticked here. Leave warehouse staff off: they see
            only the lists they made or are on, including their requests. Anyone left off who is assigned something on a
            week's list sees that list with just their own tasks on it, so assigning them a task always reaches them.
          </p>
        </div>
        {peopleList(weeklyOn, (email, on) => setPref(email, { weekly_list: on }))}
      </div>

      <div className="tn-rule">
        <div className="tn-rule-text">
          <span className="tn-rule-title">Morning email digest</span>
          <p className="tn-rule-desc">A short email at 8:30am on working days — not weekends, holidays or Dec 24 – Jan 2 — with each person's overdue, due-today and open tasks. Sent only when they have something open.</p>
        </div>
        {peopleList(digestOn, (email, on) => setPref(email, { digest_enabled: on }))}
      </div>

      {status && <p className={status.kind === 'ok' ? 'success-text' : 'error-text'}>{status.text}</p>}
      <button type="button" className="action-button admin-save-button" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save Notification Settings'}
      </button>
    </div>
  );
};

export default TaskNotificationSettings;
